import React, { useContext, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { createModal } from "react-modal-promise";
import { moduleListService } from "../../services/module-list.service";
import { authenticationService } from "../../services/authentication.service";
import LogicContext from "../../Context/LogicContext";
import GeneralModal from "../../helpers/GeneralModal";
import { GetParentMenu, SetParentMenu, GetParentMenuEdit, SetParentMenuEdit } from "../../store/parentMenuAtom";


const AddParentMenuPage = () => {
    const myPromiseModal = createModal(GeneralModal);
    const logicContext = useContext(LogicContext);
    const navigate = useNavigate();

    const getParentMenuData = GetParentMenu();
    const setParentMenuData = SetParentMenu();
    const editData = GetParentMenuEdit();
    const setParentMenuEdit = SetParentMenuEdit();
    const isEditMode = !!editData?.menu_parent_id;

    const moduleList = getParentMenuData?.module_info || [];

    const { register, handleSubmit, setValue, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            module_id: "",
            menu_parent_name: "",
            menu_parent_print_sequence: "",
            menu_parent_status: "A",
        },
    });

    useEffect(() => {
        if (moduleList.length === 0) {
            loadModules();
        }
    }, []);

    useEffect(() => {
        if (isEditMode && moduleList.length > 0) {
            setValue("module_id", String(editData.module_id));
            setValue("menu_parent_name", editData.menu_parent_name);
            setValue("menu_parent_print_sequence", editData.menu_parent_print_sequence);
            setValue("menu_parent_status", editData.status_id);
        }
    }, [isEditMode, moduleList.length]);

    const loadModules = useCallback(async () => {
        try {
            const response = await moduleListService.getParentMenuList();
            if (response?.data) setParentMenuData(response.data);
        } catch (err) {
            console.error(err);
            logicContext.showToast("Failed to load module list", "error");
        }
    }, []);

    const handleCancel = () => {
        setParentMenuEdit({});
        navigate(-1);
    };

    const onSubmit = async (formData) => {
        const confirmed = await myPromiseModal({
            title: isEditMode ? "Update Parent Menu" : "Save Parent Menu",
            body: isEditMode ? "Would you like to update this parent menu?" : "Would you like to save this parent menu?",
            showInput: false,
        });
        if (!confirmed?.decision) return;

        const authUser = authenticationService.currentUserObject();

        const payload = {
            module_id: Number(formData.module_id),
            menu_parent_name: formData.menu_parent_name.trim(),
            menu_parent_print_sequence: Number(formData.menu_parent_print_sequence),
            menu_parent_status: formData.menu_parent_status,
            user_id: authUser?.User_Id,
            option: isEditMode ? 2 : 1,
            ...(isEditMode && { menu_parent_id: editData.menu_parent_id }),
        };

        const response = await moduleListService.saveParentMenu(payload);
        const result = logicContext.httpResponseManager(
            response.data?.[0] || response.data?.result_status?.[0]
        );

        if (result.message_id === 1) {
            logicContext.showToast(
                result.message_text || (isEditMode ? "Parent Menu updated successfully!" : "Parent Menu created successfully!"),
                "success"
            );
            setParentMenuEdit({});
            sessionStorage.setItem("reloadPage", "true");
        } else {
            logicContext.showToast(result.message_text || "Operation failed", "error");
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-2xl mx-auto">

                {/* Header */}
                <div className="mb-6">
                    <button
                        onClick={handleCancel}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-3"
                    >
                        ← Back
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800">
                        {isEditMode ? "Edit Parent Menu" : "Add New Parent Menu"}
                    </h2>
                    <p className="text-sm text-gray-400 mt-0.5">
                        {isEditMode
                            ? "Update the details for this parent menu entry"
                            : "Fill in the details to create a new parent menu entry"}
                    </p>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className="space-y-5">

                            {/* Module */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Module <span className="text-red-500">*</span>
                                </label>
                                <select
                                    {...register("module_id", { required: "Please select a module" })}
                                    className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                                        ${errors.module_id ? "border-red-400 focus:ring-red-400" : "border-gray-200"}`}
                                >
                                    <option value="">— Select Module —</option>
                                    {moduleList.map((mod) => (
                                        <option key={mod.module_id} value={mod.module_id}>
                                            {mod.module_name}
                                        </option>
                                    ))}
                                </select>
                                {errors.module_id && (
                                    <p className="mt-1 text-xs text-red-500">{errors.module_id.message}</p>
                                )}
                            </div>

                            {/* Parent Menu Name */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Parent Menu Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    {...register("menu_parent_name", {
                                        required: "Parent menu name is required",
                                        minLength: { value: 2, message: "Name must be at least 2 characters" },
                                        maxLength: { value: 100, message: "Name must be 100 characters or fewer" },
                                    })}
                                    placeholder="e.g. General Setup"
                                    className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                                        ${errors.menu_parent_name ? "border-red-400 focus:ring-red-400" : "border-gray-200"}`}
                                />
                                {errors.menu_parent_name && (
                                    <p className="mt-1 text-xs text-red-500">{errors.menu_parent_name.message}</p>
                                )}
                            </div>

                            {/* Print Sequence */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Print Sequence <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    {...register("menu_parent_print_sequence", {
                                        required: "Print sequence is required",
                                        min: { value: 1, message: "Sequence must be at least 1" },
                                    })}
                                    placeholder="e.g. 1"
                                    className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                                        ${errors.menu_parent_print_sequence ? "border-red-400 focus:ring-red-400" : "border-gray-200"}`}
                                />
                                {errors.menu_parent_print_sequence && (
                                    <p className="mt-1 text-xs text-red-500">{errors.menu_parent_print_sequence.message}</p>
                                )}
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <select
                                    {...register("menu_parent_status", { required: "Status is required" })}
                                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                >
                                    <option value="A">Active</option>
                                    <option value="I">Inactive</option>
                                </select>
                            </div>

                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 mt-8 pt-5 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="px-5 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm"
                            >
                                {isSubmitting ? "Saving…" : isEditMode ? "Update Parent Menu" : "Save Parent Menu"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default React.memo(AddParentMenuPage);
